import {
  ActionIcon,
  Checkbox,
  Drawer,
  InputWrapper,
  MantineThemeOverride,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  VariantColorsResolver,
  defaultVariantColorsResolver,
  parseThemeColor,
  rem,
  rgba,
} from "@mantine/core";
import { DateInput, DateTimePicker } from "@mantine/dates";
import { Dropzone } from "@mantine/dropzone";
import classes from "./components.module.css";
import checkboxClasses from "./mantine/checkbox.module.css";
import multiSelectClasses from "./mantine/multi-select.module.css";
import selectClasses from "./mantine/select.module.css";

const variantColorResolver: VariantColorsResolver = input => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);

  if (input.variant !== "light") {
    return defaultResolvedColors;
  }

  const parsedColor = parseThemeColor({
    color: input.color,
    theme: input.theme,
  });

  if (!parsedColor.isThemeColor || parsedColor.shade === undefined) {
    return defaultResolvedColors;
  }

  const shadeColor = input.theme.colors[parsedColor.color][parsedColor.shade];

  return {
    ...defaultResolvedColors,
    background: rgba(shadeColor, 0.1),
    hover: rgba(shadeColor, 0.12),
    color: shadeColor,
  };
};

const theme: MantineThemeOverride = {
  breakpoints: {
    xs: "320px",
    sm: "360px",
    md: "430px",
    tablet: "768px",
    lg: "1280px",
    xl: "1920px",
  },
  shadows: {
    sm: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    md: "0px 6px 4px 0px rgba(0, 0, 0, 0.25)",
  },
  fontSizes: {
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(18),
    xl: rem(20),
  },
  lineHeights: {
    xs: "1.4",
    sm: "1.55",
    md: "1.55",
    lg: "1.6",
    xl: "1.65",
  },
  spacing: {
    xs: "0.625rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.25rem",
    xl: "1.5rem",
  },
  other: {
    asteriskColor: "#F30505",
  },
  colors: {
    temperature: [
      "#425AA4",
      "#A0E5F6",
      "#34BBFF",
      "#FFAF57",
      "#4DC572",
      "#B93633",
      "",
      "",
      "",
      "",
    ],
    blue: [
      "#E2EEFF",
      "#A7C7EB",
      "#3379C8",
      "#1D6DC7",
      "#0762C8",
      "#1859A3",
      "#225186",
      "#27496F",
      "#0F4A8A",
      "#2A3C50",
    ],
    gray: [
      "#F6F6F6",
      "#DBDBDB",
      "#C9C9C9",
      "#B7B7B7",
      "#A6A6A6",
      "#959595",
      "#868686",
      "#727272",
      "#1D1D1B",
      "#585858",
    ],
    green: [
      "#4EB573",
      "#BEE0CA",
      "#27B058",
      "#13AF4C",
      "#00B140",
      "#108F3E",
      "#1A763B",
      "#1F6237",
      "#0C7631",
      "#234530",
    ],
    red: [
      "#DDB8B1",
      "#FEC5BB",
      "#DC7E6D",
      "#E65D45",
      "#FA3816",
      "#D8391D",
      "#B23F2B",
      "#944133",
      "#9C2D18",
      "#6A3F37",
    ],
    yellow: [
      "#E2DEBC",
      "#FFEF73",
      "#DFD377",
      "#E8D64F",
      "#FBE122",
      "#E2CA1D",
      "#B9A92C",
      "#9A8E34",
      "#AA9A20",
      "#6E6839",
    ],
  },
  defaultRadius: 4,
  primaryColor: "gray",
  primaryShade: 4,
  variantColorResolver,
  fontFamily: "Work Sans",
  components: {
    Checkbox: Checkbox.extend({
      classNames: {
        root: checkboxClasses.root,
        input: checkboxClasses.input,
        label: checkboxClasses.label,
      },
    }),

    Text: Text.extend({
      defaultProps: {
        fz: "sm",
        lh: "sm",
      },
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        c: "gray",
        bg: "inherit",
      },
    }),
    DateInput: DateInput.extend({
      styles: theme => ({
        root: {
          position: "relative",
          color: theme.colors[theme.primaryColor][8],
          backgroundColor: theme.colors[theme.primaryColor][0],
        },
        input: {
          fontFamily: "Work Sans",
          minHeight: 42,
        },
        label: {
          position: "absolute",
          pointerEvents: "none",
          fontSize: theme.fontSizes.xs,
          marginLeft: theme.spacing.sm,
          paddingLeft: `calc(${theme.spacing.xs} / 2)`,
          paddingRight: `calc(${theme.spacing.xs} / 2)`,
          backgroundColor: theme.white,
          top: -9,
          zIndex: 1,
        },
      }),
    }),
    DateTimePicker: DateTimePicker.extend({
      classNames: {
        root: classes.dateTimePickerRoot,
        input: classes.dateTimePickerInput,
        label: classes.dateTimePickerLabel,
      },
    }),
    Modal: Modal.extend({
      styles: () => ({
        title: {
          fontFamily: "Work Sans",
        },
      }),
    }),
    Drawer: Drawer.extend({
      defaultProps: {
        withCloseButton: false,
      },
      classNames: {
        root: classes.drawerRoot,
        content: classes.drawerContent,
        body: classes.drawerBody,
        inner: classes.drawerInner,
      },
    }),
    MultiSelect: MultiSelect.extend({
      classNames: {
        root: multiSelectClasses.multiSelectRoot,
        dropdown: multiSelectClasses.multiSelectDropdown,
        pill: multiSelectClasses.multiSelectPill,
        pillsList: multiSelectClasses.multiSelectPillsList,
        input: multiSelectClasses.multiSelectInput,
        label: multiSelectClasses.multiSelectLabel,
        required: multiSelectClasses.multiSelectRequired,
      },
    }),
    Select: Select.extend({
      classNames: {
        root: selectClasses.selectRoot,
        label: selectClasses.selectLabel,
        input: selectClasses.selectInput,
        dropdown: selectClasses.selectDropdown,
        required: selectClasses.selectRequired,
        option: selectClasses.selectOption,
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        color: "white",
        radius: 0,
      },
    }),
    InputWrapper: InputWrapper.extend({
      styles: theme => ({
        label: {
          fontFamily: "Work Sans",
          fontSize: theme.fontSizes.xs,
        },
        error: { background: "white" },
      }),
    }),
    Menu: Menu.extend({
      styles: () => ({
        itemLabel: {
          fontFamily: "Work Sans",
        },
      }),
    }),
    Textarea: Textarea.extend({
      styles: theme => ({
        root: {
          position: "relative",
          color: theme.colors[theme.primaryColor][8],
        },
        input: {
          fontFamily: "Work Sans",
        },
        label: {
          position: "absolute",
          pointerEvents: "none",
          fontSize: theme.fontSizes.xs,
          marginLeft: theme.spacing.sm,
          paddingLeft: `calc(${theme.spacing.xs} / 2)`,
          paddingRight: `calc(${theme.spacing.xs} / 2)`,
          backgroundColor: theme.white,
          top: -9,
          zIndex: 1,
        },
        required: {
          color: theme.other.asteriskColor,
        },
      }),
    }),
    TextInput: TextInput.extend({
      styles: theme => ({
        root: {
          position: "relative",
          color: theme.colors[theme.primaryColor][8],
        },
        input: {
          minHeight: 42,
          fontFamily: "Work Sans",
        },
        label: {
          position: "absolute",
          pointerEvents: "none",
          fontSize: theme.fontSizes.xs,
          marginLeft: theme.spacing.sm,
          paddingLeft: `calc(${theme.spacing.xs} / 2)`,
          paddingRight: `calc(${theme.spacing.xs} / 2)`,
          backgroundColor: theme.white,
          color: "inherit",
          top: -9,
          zIndex: 1,
        },
        required: {
          color: theme.other.asteriskColor,
        },
      }),
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        hideControls: true,
      },
      styles: theme => ({
        root: {
          position: "relative",
          color: theme.colors[theme.primaryColor][8],
        },
        input: {
          fontFamily: "Work Sans",
          minHeight: 42,
        },
        label: {
          position: "absolute",
          pointerEvents: "none",
          fontSize: theme.fontSizes.xs,
          marginLeft: theme.spacing.sm,
          paddingLeft: `calc(${theme.spacing.xs} / 2)`,
          paddingRight: `calc(${theme.spacing.xs} / 2)`,
          backgroundColor: theme.white,
          top: -9,
          zIndex: 1,
        },
      }),
    }),
    SegmentedControl: SegmentedControl.extend({
      styles: theme => ({
        root: {
          color: theme.colors[theme.primaryColor][8],
          backgroundColor: theme.colors[theme.primaryColor][0],
        },
        label: {
          fontFamily: "Work Sans",
        },
      }),
    }),
    Dropzone: Dropzone.extend({
      styles: () => ({
        root: { cursor: "pointer" },
      }),
    }),
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false,
      },
    }),
  },
};

export default theme;
